import { useEffect, useMemo, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

import type { PincodeRow } from 'views/orders/create-order/components/NewPickupAddressDialog';

type Props = {
  pincodeList: PincodeRow[];
  value: PincodeRow | null;
  onChange: (row: PincodeRow | null) => void;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

export default function PincodeAutocomplete({ pincodeList, value, onChange, error, helperText, onBlur, disabled, sx }: Props) {
  const [pincodeInputValue, setPincodeInputValue] = useState('');
  const pincodeFilter = useMemo(
    () =>
      createFilterOptions<PincodeRow>({
        limit: 100,
        stringify: (option) => `${option.PINCODE ?? ''} - ${option.CITY_NAME ?? ''}`
      }),
    []
  );

  const selectedLabel = value ? `${value.PINCODE ?? ''} - ${value.CITY_NAME ?? ''}` : '';
  useEffect(() => {
    setPincodeInputValue(selectedLabel);
  }, [value, selectedLabel]);

  return (
    <Autocomplete
      sx={{ minWidth: 280, ...sx }}
      options={pincodeList}
      disabled={disabled}
      value={value}
      inputValue={pincodeInputValue}
      onInputChange={(_event, next, reason) => {
        setPincodeInputValue(next);
      }}
      onOpen={() => setPincodeInputValue(selectedLabel)}
      onChange={(_event, row) => {
        onChange(row);
        setPincodeInputValue(row ? `${row.PINCODE ?? ''} - ${row.CITY_NAME ?? ''}` : '');
      }}
      filterOptions={(options, state) =>
        pincodeFilter(options, {
          ...state,
          inputValue: pincodeInputValue
        })
      }
      getOptionLabel={(o) => `${o.PINCODE ?? ''} - ${o.CITY_NAME ?? ''}`}
      isOptionEqualToValue={(a, b) => a.ID === b.ID}
      renderInput={(params) => (
        <TextField {...params} label="Pincode" required error={error} helperText={helperText} onBlur={onBlur} />
      )}
    />
  );
}